import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import VerifiedIcon from "@mui/icons-material/Verified";
import { teal } from "@mui/material/colors";

const eurFormatter = new Intl.NumberFormat("ro-RO", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const ronFormatter = new Intl.NumberFormat("ro-RO", {
  style: "currency",
  currency: "RON",
  maximumFractionDigits: 0,
});

const formatRon = (value) => {
  const number = Number(value || 0);
  return Number.isFinite(number) && number > 0 ? ronFormatter.format(number) : "-";
};

const formatEur = (value) => eurFormatter.format(Number(value || 0));

export default function InvestitiiFinantareCard({
  enabled,
  poolId,
  pools,
  loadingPools,
  saving,
  checkingAssigned,
  message,
  assignedProduct,
  productSnapshot,
  canSave,
  onToggleEnabled,
  onChangePoolId,
  onSave,
}) {
  const hasPools = Array.isArray(pools) && pools.length > 0;
  const isAssigned = Boolean(assignedProduct?.assigned);
  const selectedPool = pools.find(
    (pool) => String(pool.id) === String(poolId || assignedProduct?.poolId || ""),
  );
  const assignedOwner = [
    assignedProduct?.investorName || selectedPool?.investorName,
    assignedProduct?.poolName || selectedPool?.name,
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <Card
      sx={{
        width: "100%",
        mb: 2,
        borderRadius: 2,
        overflow: "hidden",
        border: isAssigned
          ? "1px solid rgba(22, 163, 74, 0.35)"
          : "1px solid rgba(13, 148, 136, 0.22)",
        boxShadow: isAssigned
          ? "0 20px 60px rgba(22, 163, 74, 0.14)"
          : "0 18px 50px rgba(15, 23, 42, 0.08)",
      }}
    >
      <CardHeader
        avatar={
          <Avatar sx={{ bgcolor: teal[600] }}>
            <AccountBalanceWalletIcon fontSize="small" />
          </Avatar>
        }
        title={
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Typography sx={{ fontWeight: 850, letterSpacing: 0.2 }}>
              Investiții
            </Typography>
            {isAssigned ? (
              <Chip
                size="small"
                color="success"
                icon={<CheckCircleIcon />}
                label="Asignat"
                sx={{ fontWeight: 800 }}
              />
            ) : null}
          </Stack>
        }
        subheader={
          <Typography sx={{ color: "#667085", fontWeight: 600 }}>
            {isAssigned
              ? "Status: vizibil în Produse finanțate"
              : "Status: pregătit pentru alocare"}
          </Typography>
        }
      />
      <CardContent
        sx={{
          borderTop: "1px solid rgba(13, 148, 136, 0.14)",
          background:
            "linear-gradient(135deg, rgba(240,253,250,0.76), rgba(255,255,255,0.98))",
        }}
      >
        <Stack spacing={1.5}>
          {checkingAssigned ? (
            <Alert severity="info" icon={<CircularProgress size={16} />}>
              Verific dacă produsul este deja finanțat...
            </Alert>
          ) : null}

          {isAssigned ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                border: "1px solid rgba(22, 163, 74, 0.26)",
                background:
                  "linear-gradient(135deg, rgba(220,252,231,0.92), rgba(240,253,244,0.98))",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.25}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1.1} alignItems="center">
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "8px",
                      display: "grid",
                      placeItems: "center",
                      color: "#067647",
                      background: "#dcfce7",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <VerifiedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ color: "#067647", fontWeight: 900 }}>
                      Asignat în Investiții
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#344054" }}>
                      {assignedOwner || "Pool investiții"} · status{" "}
                      {assignedProduct?.status || "în stoc"}
                    </Typography>
                  </Box>
                </Stack>
                <Chip
                  size="small"
                  label={assignedProduct?.sku || productSnapshot.sku || "SKU"}
                  sx={{
                    borderRadius: "8px",
                    background: "#fff",
                    color: "#067647",
                    border: "1px solid #bbf7d0",
                    fontWeight: 850,
                  }}
                />
              </Stack>
            </Box>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={enabled || isAssigned}
                disabled={isAssigned}
                onChange={(event) => onToggleEnabled(event.target.checked)}
              />
            }
            label="Finanțat?"
            sx={{
              alignSelf: "flex-start",
              "& .MuiFormControlLabel-label": {
                fontWeight: 850,
                color: "#101828",
              },
            }}
          />

          {enabled || isAssigned ? (
            <>
              {!isAssigned ? (
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", md: "center" }}
                >
                  <FormControl fullWidth size="small" disabled={loadingPools || saving}>
                    <InputLabel>Finanțat din</InputLabel>
                    <Select
                      label="Finanțat din"
                      value={poolId}
                      onChange={(event) => onChangePoolId(event.target.value)}
                    >
                      {loadingPools ? (
                        <MenuItem value="" disabled>
                          Se încarcă pool-urile...
                        </MenuItem>
                      ) : null}
                      {!loadingPools && !hasPools ? (
                        <MenuItem value="" disabled>
                          Nu există pool-uri active
                        </MenuItem>
                      ) : null}
                      {pools.map((pool) => (
                        <MenuItem key={pool.id} value={pool.id}>
                          {pool.investorName} / {pool.name} / cash disponibil{" "}
                          {formatEur(pool.metrics?.cashAvailable)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={
                      saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                    }
                    onClick={onSave}
                    disabled={!canSave || saving || loadingPools}
                    sx={{
                      minHeight: 40,
                      px: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 850,
                      background:
                        "linear-gradient(135deg, #0d9488 0%, #2563eb 100%)",
                      boxShadow: "none",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #0f766e 0%, #1d4ed8 100%)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {saving ? "Salvez..." : "Save"}
                  </Button>
                </Stack>
              ) : null}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 1,
                }}
              >
                <TextField
                  size="small"
                  label="SKU"
                  value={productSnapshot.sku || ""}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Nr contract"
                  value={productSnapshot.contractId || ""}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Brand"
                  value={productSnapshot.brand || ""}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Model"
                  value={productSnapshot.model || ""}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Preț intrare"
                  value={formatRon(productSnapshot.purchasePriceRon)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  size="small"
                  label="Preț vânzare"
                  value={formatRon(productSnapshot.listingPriceRon)}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </>
          ) : null}

          {message?.text ? (
            <Alert severity={message.type || "info"} sx={{ borderRadius: 2 }}>
              {message.text}
            </Alert>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}
